import React from "react";
import { useRouteError } from "react-router-dom";

const styles = {
  wrapper: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    background: "linear-gradient(145deg, #f8f9ff 0%, #eef0ff 50%, #f0f2ff 100%)",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
  },
  card: {
    maxWidth: 480,
    width: "100%",
    padding: "48px 40px",
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 4px 24px rgba(30, 34, 251, 0.08), 0 12px 48px rgba(0,0,0,0.06)",
    textAlign: "center",
  },
  icon: {
    width: 72,
    height: 72,
    margin: "0 auto 24px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #1E22FB 0%, #7375FD 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 36,
  },
  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 600,
    color: "#1a1a2e",
    letterSpacing: "-0.02em",
  },
  message: {
    margin: "12px 0 28px",
    fontSize: 15,
    lineHeight: 1.55,
    color: "#64748b",
  },
  detail: {
    marginBottom: 28,
    padding: "14px 16px",
    borderRadius: 10,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    fontSize: 13,
    fontFamily: "ui-monospace, monospace",
    color: "#475569",
    textAlign: "left",
    maxHeight: 120,
    overflow: "auto",
    wordBreak: "break-word",
  },
  button: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "12px 24px",
    fontSize: 15,
    fontWeight: 600,
    color: "#fff",
    background: "linear-gradient(135deg, #1E22FB 0%, #0306BA 100%)",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    boxShadow: "0 4px 14px rgba(30, 34, 251, 0.35)",
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
  },
};

/** Fallback UI used by both the class ErrorBoundary and the router errorElement */
export function ErrorFallbackUI({ error, errorInfo, onReload }) {
  const message = error?.message || "Something went wrong.";
  const detail = errorInfo?.componentStack ?? (error?.stack ?? String(error));

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <div style={styles.icon} aria-hidden>
          ⚠
        </div>
        <h1 style={styles.title}>Something went wrong</h1>
        <p style={styles.message}>
          We’re sorry — something unexpected happened. Try refreshing the page, or head back to the home page.
        </p>
        {process.env.NODE_ENV === "development" && detail && (
          <pre style={styles.detail}>{detail}</pre>
        )}
        <button
          type="button"
          style={styles.button}
          onClick={onReload}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow = "0 6px 20px rgba(30, 34, 251, 0.4)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 4px 14px rgba(30, 34, 251, 0.35)";
          }}
        >
          Back to home
        </button>
      </div>
    </div>
  );
}

/** Use as errorElement in React Router createBrowserRouter */
export function RouterErrorFallback() {
  const error = useRouteError();
  const handleReload = () => {
    window.location.href = "/";
  };
  return (
    <ErrorFallbackUI
      error={error instanceof Error ? error : new Error(String(error))}
      errorInfo={error?.stack ? { componentStack: error.stack } : null}
      onReload={handleReload}
    />
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    if (typeof this.props.onError === "function") {
      this.props.onError(error, errorInfo);
    }
  }

  handleReload = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <ErrorFallbackUI
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReload={this.handleReload}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
